import { DEFAULT_COLORS } from '../constants';
import HorizontalTimeline from './HorizontalTimeline';
import VerticalTimeline from './VerticalTimeline';
import ZigzagTimeline from './ZigzagTimeline';
import StorylineTimeline from './StorylineTimeline';
import NewspaperTimeline from './NewspaperTimeline';
import MapTimeline from './MapTimeline';
import SwimlaneTimeline from './SwimlaneTimeline';
import TreeTimeline from './TreeTimeline';
import FilmstripTimeline from './FilmstripTimeline';
import SpineTimeline from './SpineTimeline';

// ─── TIMELINE CANVAS COMPONENT ────────────────────────────────────────────────
//
//  Wrapper that applies the timeline's background (color or image) and renders
//  the appropriate layout component based on `orientation`.
//
//  Storyline and Spine are reading layouts — they flow as plain page content
//  with no canvas background or border-radius box.
//

const TimelineCanvas = ({ timeline, onEventClick, onReorderLanes, canEdit, onEditEvent, onAddEventToChapter, onColorsChange, isEmbed = false }) => {
  const colors      = timeline.colors || DEFAULT_COLORS;
  const { bgImage, orientation = 'horizontal', manualOrder = false } = timeline;

  // Horizontal-scroll layouts need overflow:hidden to clip their wide content.
  const needsClip = ['horizontal', 'filmstrip', 'map', 'swimlane'].includes(orientation);

  // Reading layouts (Storyline, Spine) render as plain page content — no canvas
  // background, no border-radius, no min-height wrapper.
  const isReadingLayout = orientation === 'storyline' || orientation === 'spine';

  const renderLayout = () => {
    if (orientation === 'horizontal') {
      return <HorizontalTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} timeline={timeline} isEmbed={isEmbed} chapters={timeline.chapters || []} onColorsChange={onColorsChange} />;
    }
    if (orientation === 'zigzag') {
      return <ZigzagTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} timeline={timeline} isEmbed={isEmbed} />;
    }
    if (orientation === 'storyline') {
      return (
        <StorylineTimeline
          events={timeline.events}
          colors={colors}
          onEventClick={onEventClick}
          manualOrder={manualOrder}
          chapters={timeline.chapters || []}
          timeline={timeline}
          isEmbed={isEmbed}
          canEdit={canEdit}
          onAddEventToChapter={onAddEventToChapter}
        />
      );
    }
    if (orientation === 'newspaper') {
      return <NewspaperTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} timeline={timeline} isEmbed={isEmbed} canEdit={canEdit} />;
    }
    if (orientation === 'map') {
      return <MapTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} mapCenter={timeline.mapCenter || null} timeline={timeline} isEmbed={isEmbed} canEdit={canEdit} onColorsChange={onColorsChange} />;
    }
    if (orientation === 'swimlane') {
      return <SwimlaneTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} swimlanes={timeline.swimlanes || []} swimlaneColors={timeline.swimlaneColors || {}} onReorderLanes={onReorderLanes} timeline={timeline} isEmbed={isEmbed} />;
    }
    if (orientation === 'tree') {
      return <TreeTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} timeline={timeline} isEmbed={isEmbed} />;
    }
    if (orientation === 'filmstrip') {
      return (
        <FilmstripTimeline
          events={timeline.events}
          colors={colors}
          onEventClick={onEventClick}
          canEdit={canEdit}
          onEditEvent={onEditEvent}
          transitionStyle={timeline.filmstripTransition || 'fade'}
          autoPlaySpeed={timeline.filmstripSpeed || 4000}
          timeline={timeline}
          isEmbed={isEmbed}
        />
      );
    }
    if (orientation === 'spine') {
      return (
        <SpineTimeline
          events={timeline.events}
          colors={colors}
          onEventClick={onEventClick}
          manualOrder={manualOrder}
          timeline={timeline}
          canEdit={canEdit}
          onEditEvent={onEditEvent}
        />
      );
    }
    return <VerticalTimeline events={timeline.events} colors={colors} onEventClick={onEventClick} manualOrder={manualOrder} timeline={timeline} isEmbed={isEmbed} />;
  };

  if (isReadingLayout) {
    return <div>{renderLayout()}</div>;
  }

  const bgStyle = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: colors.tertiary };

  return (
    <div style={{
      ...bgStyle,
      borderRadius: 'var(--radius-xl)',
      position: 'relative',
      overflow: needsClip ? 'hidden' : 'visible',
      minHeight: 300,
    }}>
      {bgImage && (
        <div style={{ position: 'absolute', inset: 0, background: `${colors.tertiary}cc`, borderRadius: 'inherit' }} />
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {renderLayout()}
      </div>
    </div>
  );
};

export default TimelineCanvas;
